import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Switch,
  FormControlLabel,
  MenuItem,
  Select,
  InputLabel,
  FormControl
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import axios from 'axios';
import Swal from 'sweetalert2';
import AppNavbar from './AdminNav';

const initialFormData = {
  projectName: '',
  description: '',
  projectStatus: 'initiated',
  startDate: '',
  endDate: '',
  client: {
    name: '',
    organization: '',
    email: '',
    contact: '',
    address: '',
  },
  projectThumbnail: null,
};

const getToken = () => localStorage.getItem('token');

const ProjectDetails = () => {
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const token = getToken();
      const response = await axios.get('https://backend.ytctechventures.com/admin/getProjectList', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProjects(response.data.projectList);
    } catch (error) {
      console.error('Error fetching projects:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to fetch projects. Please try again later.',
      });
    }
  };

  const handleToggleStatus = async (project) => {
    try {
      const token = getToken();
      const newStatus = project.projectStatus === 'completed' ? 'initiated' : 'completed';
      const flag = newStatus === 'completed' ? 1 : 0;

      const response = await axios.patch(
        'https://backend.ytctechventures.com/admin/updateStatus',
        { projectId: project.projectId, projectStatus: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            flag: flag,
          },
        }
      );

      Swal.fire({
        icon: 'success',
        title: 'Status Updated',
        text: response.data.message || `Project status updated to ${newStatus}.`,
      });

      setProjects((prevProjects) =>
        prevProjects.map((p) =>
          p.projectId === project.projectId ? { ...p, projectStatus: newStatus } : p
        )
      );
    } catch (error) {
      console.error('Error updating status:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to update status.',
      });
    }
  };

  const handleOpen = () => {
    setIsEditMode(false);
    setFormData(initialFormData);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData(initialFormData);
    setIsEditMode(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('client.')) {
      const key = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        client: {
          ...prev.client,
          [key]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = getToken();
      const data = new FormData();

       if (isEditMode && formData.projectId) {
          data.append('projectId', formData.projectId); 
        } else {
          data.append('projectStatus', formData.projectStatus);
        }


      data.append('projectName', formData.projectName);
      data.append('description', formData.description);
      data.append('startDate', formData.startDate);
      data.append('endDate', formData.endDate);

      Object.entries(formData.client).forEach(([key, value]) => {
        data.append(`client[${key}]`, value);
      });

      if (formData.projectThumbnail) {
        data.append('projectThumbnail', formData.projectThumbnail);
      }

      let response;
      if (isEditMode) {
        response = await axios.patch(
          'https://backend.ytctechventures.com/admin/updateProjectDetails',
          data,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        Swal.fire({
          icon: 'success',
          title: 'Project Updated',
          text: response.data.message || 'Project updated successfully.',
        });
      } else {
        response = await axios.post(
          'https://backend.ytctechventures.com/admin/createProject',
          data,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        Swal.fire({
          icon: 'success',
          title: 'Project Created',
          text: response.data.message || 'New project created successfully!',
        });
      }

      handleClose();
      fetchProjects();
    } catch (error) {
      let message = 'Something went wrong while submitting the project.';
      if (error.response) {
        message = error.response.data?.message || message;
      } else if (error.message) {
        message = error.message;
      }

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: message,
      });
    }
  };

  const handleEditClick = (project) => {
    setFormData({
      projectId: project.projectId || '',
      projectName: project.projectName || '',
      description: project.description || '',
      projectStatus: project.projectStatus || 'initiated',
      startDate: project.startDate ? new Date(project.startDate).toISOString().split('T')[0] : '',
      endDate: project.endDate ? new Date(project.endDate).toISOString().split('T')[0] : '',
      client: {
        name: project.client?.name || '',
        organization: project.client?.organization || '',
        email: project.client?.email || '',
        contact: project.client?.contact || '',
        address: project.client?.address || '',
      },
      projectThumbnail: null,
    });
    setIsEditMode(true);
    setOpen(true);
  };

  return (
    <>
      <AppNavbar />
      <Box sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Project Details
        </Typography>

        <Button variant="contained" color="primary" onClick={handleOpen} sx={{ mb: 2 }}>
          Add New Project
        </Button>

        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 4, maxHeight: '75vh', overflow: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f0f0f0' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Project ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Start Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>End Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Client Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Organization</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Contact</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Address</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.map((project, index) => (
                <TableRow
                  key={project._id}
                  sx={{
                    backgroundColor: index % 2 === 0 ? '#fcfcfc' : '#fff',
                    transition: 'all 0.3s ease',
                    '&:hover': { backgroundColor: '#eef6ff' },
                  }}
                >
                  <TableCell>{project.projectId}</TableCell>
                  <TableCell>{project.projectName}</TableCell>
                  <TableCell sx={{ maxWidth: 150, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {project.description}
                  </TableCell>
                  <TableCell>{project.startDate ? new Date(project.startDate).toLocaleDateString() : ''}</TableCell>
                  <TableCell>{project.endDate ? new Date(project.endDate).toLocaleDateString() : ''}</TableCell>
                  <TableCell>{project.client?.name}</TableCell>
                  <TableCell>{project.client?.organization}</TableCell>
                  <TableCell>{project.client?.email}</TableCell>
                  <TableCell>{project.client?.contact}</TableCell>
                  <TableCell>{project.client?.address}</TableCell>
                  <TableCell>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={project.projectStatus === 'completed'}
                          onChange={() => handleToggleStatus(project)}
                          color="success"
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          color={project.projectStatus === 'completed' ? 'green' : 'orange'}
                          sx={{ fontWeight: 'bold' }}
                        >
                          {project.projectStatus.toUpperCase()}
                        </Typography>
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton onClick={() => handleEditClick(project)} color="primary">
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Dialog for Add/Edit Project */}
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
          <DialogTitle>{isEditMode ? 'Edit Project' : 'Add New Project'}</DialogTitle>
          <DialogContent>
            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <TextField
                label="Project Name"
                name="projectName"
                value={formData.projectName}
                onChange={handleInputChange}
                required
                fullWidth
              />

              <TextField
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={3}
                fullWidth
              />

              {/* Project Status Dropdown */}
              <FormControl fullWidth>
                <InputLabel id="project-status-label">Project Status</InputLabel>
                <Select
                  labelId="project-status-label"
                  name="projectStatus"
                  value={formData.projectStatus}
                  label="Project Status"
                  onChange={handleInputChange}
                >
                  <MenuItem value="initiated">Initiated</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Start Date"
                name="startDate"
                type="date"
                value={formData.startDate}
                onChange={handleInputChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End Date"
                name="endDate"
                type="date"
                value={formData.endDate}
                onChange={handleInputChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />

              <Typography variant="h6" sx={{ mt: 2 }}>
                Client Information
              </Typography>
              <TextField
                label="Client Name"
                name="client.name"
                value={formData.client.name}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                label="Organization"
                name="client.organization"
                value={formData.client.organization}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                label="Email"
                name="client.email"
                type="email"
                value={formData.client.email}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                label="Contact"
                name="client.contact"
                value={formData.client.contact}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                label="Address"
                name="client.address"
                value={formData.client.address}
                onChange={handleInputChange}
                multiline
                rows={2}
                fullWidth
              />

              {/* Project Thumbnail File */}
              <Box>
                <Button variant="outlined" component="label" fullWidth>
                  {formData.projectThumbnail ? 'Change Project Thumbnail' : 'Upload Project Thumbnail'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      setFormData((prev) => ({
                        ...prev,
                        projectThumbnail: file,
                      }));
                    }}
                  />
                </Button>
                {formData.projectThumbnail && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Selected file: {formData.projectThumbnail.name}
                  </Typography>
                )}
              </Box>

              <DialogActions sx={{ px: 0 }}>
                <Button onClick={handleClose} color="secondary">
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="primary">
                  {isEditMode ? 'Update Project' : 'Create Project'}
                </Button>
              </DialogActions>
            </Box>
          </DialogContent>
        </Dialog>
      </Box>
    </>
  );
};

export default ProjectDetails;
